import type { GameSession } from '@business-game/shared';

const GAME_MODE_LABELS: Record<string, { fr: string; en: string }> = {
  teamlab: { fr: 'Team Lab', en: 'Team Lab' },
  leadlab: { fr: 'Lead Lab', en: 'Lead Lab' },
};

const LANGUAGE_LABELS: Record<string, { fr: string; en: string }> = {
  fr: { fr: 'Français', en: 'French' },
  en: { fr: 'English', en: 'English' },
  es: { fr: 'Español', en: 'Spanish' },
  de: { fr: 'Deutsch', en: 'German' },
  nl: { fr: 'Nederlands', en: 'Dutch' },
  pt: { fr: 'Português', en: 'Portuguese' },
};

export type SessionShareMessageLocale = 'fr' | 'en';

export type SessionShareLinks = {
  animation: string;
  direct: string;
  players: Array<{ teamNumber: number; url: string }>;
};

export const buildSessionShareLinks = (sessionCode: string, origin = window.location.origin): SessionShareLinks => {
  const code = sessionCode.toUpperCase();
  return {
    animation: `${origin}/admin/decisions/0?session=${code}`,
    direct: `${origin}/select-player/${code}`,
    players: [1, 2, 3, 4].map((teamNumber) => ({
      teamNumber,
      url: `${origin}/select-player/${code}?equipe=${teamNumber}`,
    })),
  };
};

export const buildSessionShareMessage = (
  session: GameSession,
  options?: { origin?: string; locale?: SessionShareMessageLocale }
): string => {
  const locale = options?.locale ?? 'fr';
  const origin = options?.origin ?? window.location.origin;
  const code = session.code.toUpperCase();
  const links = buildSessionShareLinks(code, origin);
  const sessionName = session.name?.trim() || code;
  const facilitator = session.notes?.trim() || '—';
  const gameMode =
    GAME_MODE_LABELS[session.gameMode]?.[locale] ?? session.gameMode;
  const language =
    LANGUAGE_LABELS[session.language ?? 'fr']?.[locale] ?? session.language ?? 'Français';

  if (locale === 'en') {
    return [
      'Hello,',
      '',
      `Here are the details for session ${sessionName} (${code}):`,
      '',
      `• Game mode: ${gameMode}`,
      `• Language: ${language}`,
      `• Facilitator: ${facilitator}`,
      '',
      'Facilitator dashboard link:',
      links.animation,
      '',
      'Direct player link:',
      links.direct,
      '',
      'Team links:',
      ...links.players.map(({ teamNumber, url }) => `• Player ${teamNumber}: ${url}`),
      '',
      'Have a great session!',
    ].join('\n');
  }

  return [
    'Bonjour,',
    '',
    `Voici les informations pour la session ${sessionName} (${code}) :`,
    '',
    `• Mode : ${gameMode}`,
    `• Langue : ${language}`,
    `• Animateur : ${facilitator}`,
    '',
    `Lien d'animation (tableau de bord animateur) :`,
    links.animation,
    '',
    `Lien direct joueurs :`,
    links.direct,
    '',
    `Liens par équipe :`,
    ...links.players.map(({ teamNumber, url }) => `• Joueur ${teamNumber} : ${url}`),
    '',
    `Bonne session !`,
  ].join('\n');
};
